/**
 * Logger utility with environment-aware behavior.
 * - Development: logs to console at all levels.
 * - Production: routes all levels to Sentry for observability.
 */

import {
  addBreadcrumb,
  captureException,
  captureMessage,
} from '@/modules/sentry/helpers/capture';

/**
 * Log levels for the logger
 */
export enum ELogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

/**
 * Logger interface for consistent logging across the application
 */
interface ILogger {
  /**
   * Log debug messages
   */
  debug: (message: string, context?: Record<string, unknown>) => void;

  /**
   * Log info messages
   */
  info: (message: string, context?: Record<string, unknown>) => void;

  /**
   * Log warning messages
   */
  warn: (message: string, context?: Record<string, unknown>) => void;

  /**
   * Log error messages
   */
  error: (
    message: string,
    error?: Error | unknown,
    context?: Record<string, unknown>,
  ) => void;
}

/**
 * Report to Sentry at the appropriate level.
 * - error: captureException
 * - warn: captureMessage (warning)
 * - info/debug: addBreadcrumb (no event created, but visible in error context)
 */
function reportToSentry(
  level: ELogLevel,
  message: string,
  error?: Error | unknown,
  context?: Record<string, unknown>,
): void {
  if (level === ELogLevel.ERROR) {
    const exception = error instanceof Error ? error : new Error(message);
    captureException(exception, {
      tags: { source: 'logger' },
      extra: {
        message,
        ...context,
        originalError: error,
      },
    });
  } else if (level === ELogLevel.WARN) {
    captureMessage(message, {
      level: 'warning',
      tags: { source: 'logger' },
      extra: context,
    });
  } else {
    addBreadcrumb({
      message,
      category: 'logger',
      level: level === ELogLevel.INFO ? 'info' : 'debug',
      data: context,
    });
  }
}

/**
 * Internal logging function that respects environment
 */
function log(
  level: ELogLevel,
  message: string,
  error?: Error | unknown,
  context?: Record<string, unknown>,
): void {
  if (import.meta.env.DEV) {
    const logContext = context ? { ...context } : {};

    switch (level) {
      case ELogLevel.DEBUG:
        console.debug(`[DEBUG]`, message, logContext);
        break;
      case ELogLevel.INFO:
        console.info(`[INFO]`, message, logContext);
        break;
      case ELogLevel.WARN:
        console.warn(`[WARN]`, message, logContext);
        break;
      case ELogLevel.ERROR:
        if (error) {
          console.error(`[ERROR]`, message, error, logContext);
        } else {
          console.error(`[ERROR]`, message, logContext);
        }
        break;
    }
    return;
  }

  reportToSentry(level, message, error, context);
}

/**
 * Logger instance with environment-aware behavior
 */
export const logger: ILogger = {
  debug: (message: string, context?: Record<string, unknown>) => {
    log(ELogLevel.DEBUG, message, undefined, context);
  },

  info: (message: string, context?: Record<string, unknown>) => {
    log(ELogLevel.INFO, message, undefined, context);
  },

  warn: (message: string, context?: Record<string, unknown>) => {
    log(ELogLevel.WARN, message, undefined, context);
  },

  error: (
    message: string,
    error?: Error | unknown,
    context?: Record<string, unknown>,
  ) => {
    log(ELogLevel.ERROR, message, error, context);
  },
};
